import { z } from 'zod';
import { IdentityType, Role } from '@loci/types';
import { VALIDATION_MESSAGES, validateAndFormat, ERROR_CODES } from './errors';

/** Default JWT `role` when the claim is absent (**legacy tokens**). */
const DEFAULT_JWT_ROLE = Role.USER;

/** Lowercase role strings from older JWTs — normalized to {@link Role} on parse. */
const LEGACY_JWT_ROLE_STRINGS = ['admin', 'superadmin', 'user', 'moderator'] as const;

const LEGACY_JWT_ROLE_TO_CANONICAL: Record<(typeof LEGACY_JWT_ROLE_STRINGS)[number], Role> = {
  admin: Role.ADMIN,
  superadmin: Role.SUPER_ADMIN,
  user: Role.USER,
  moderator: Role.MODERATOR,
};

/**
 * JWT `role` claim: canonical {@link Role} values plus legacy lowercase aliases.
 * Parsed output is always a {@link Role} string.
 */
export const jwtRoleClaimSchema = z
  .union([z.nativeEnum(Role), z.enum(LEGACY_JWT_ROLE_STRINGS)])
  .transform((value): Role => {
    if (value in LEGACY_JWT_ROLE_TO_CANONICAL) {
      return LEGACY_JWT_ROLE_TO_CANONICAL[value as keyof typeof LEGACY_JWT_ROLE_TO_CANONICAL];
    }
    return value as Role;
  });

/**
 * Authentication and authorization validation schemas with accessible error messages
 */

// Basic field validation with improved error messages
export const emailSchema = z
  .string()
  .email(VALIDATION_MESSAGES.INVALID_EMAIL)
  .max(255, VALIDATION_MESSAGES.FIELD.EMAIL_TOO_LONG);

export const passwordSchema = z
  .string()
  .min(8, VALIDATION_MESSAGES.TOO_SHORT(8))
  .max(128, VALIDATION_MESSAGES.FIELD.PASSWORD_TOO_LONG)
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).*$/,
    VALIDATION_MESSAGES.AUTH.WEAK_PASSWORD,
  );

export const usernameSchema = z
  .string()
  .min(3, VALIDATION_MESSAGES.TOO_SHORT(3))
  .max(30, VALIDATION_MESSAGES.TOO_LONG(30))
  .regex(/^[a-zA-Z0-9_-]+$/, VALIDATION_MESSAGES.FIELD.USERNAME_INVALID);

export const nameSchema = z
  .string()
  .min(1, VALIDATION_MESSAGES.REQUIRED)
  .max(50, VALIDATION_MESSAGES.FIELD.NAME_TOO_LONG)
  .regex(/^[a-zA-Z\s'-]+$/, VALIDATION_MESSAGES.FIELD.NAME_INVALID);

/**
 * API-1659 — client-resolved `prefers-color-scheme` used only to write the
 * `loci-theme` cookie when the account setting is `system`. Not persisted.
 */
export const resolvedThemeSchema = z.enum(['light', 'dark']);

// Authentication request schemas with improved error messages
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
  rememberMe: z.boolean().optional().default(false),
  resolvedTheme: resolvedThemeSchema.optional(),
});

export const registerSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, VALIDATION_MESSAGES.AUTH.CONFIRM_PASSWORD),
    name: nameSchema.optional(), // Matching backend expectations
    firstName: nameSchema.optional(),
    lastName: nameSchema.optional(),
    username: usernameSchema.optional(),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: VALIDATION_MESSAGES.AUTH.TERMS_REQUIRED,
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: VALIDATION_MESSAGES.PASSWORDS_DONT_MATCH,
    path: ['confirmPassword'],
  });

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, 'Reset token is required'),
    password: passwordSchema,
    confirmPassword: z.string().min(1, VALIDATION_MESSAGES.AUTH.CONFIRM_PASSWORD),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: VALIDATION_MESSAGES.PASSWORDS_DONT_MATCH,
    path: ['confirmPassword'],
  });

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, VALIDATION_MESSAGES.AUTH.CONFIRM_PASSWORD),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: VALIDATION_MESSAGES.PASSWORDS_DONT_MATCH,
    path: ['confirmPassword'],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  });

/**
 * Server-side wire contract for `PUT /api/v1/auth/change-password`.
 *
 * `confirmPassword` is a **UI-only** concern: the client form validates that the
 * two password fields match (via `changePasswordSchema` + `zodResolver`) before
 * submit, then sends only `{ currentPassword, newPassword }` over the wire. The
 * backend must therefore parse the body WITHOUT requiring `confirmPassword` —
 * otherwise the strict object rejects the missing key and every real submission
 * 400s before any work runs. The "new must differ from current" check is kept
 * because that is a genuine server-side invariant, not a UX nicety.
 */
export const changePasswordRequestSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  });

/**
 * Public contract for changing a user's email address (API-1804).
 * The body is `{ newEmail }` only. Step-up (passkey, else a fresh magic link)
 * is enforced on the session, not by a password field. A legacy
 * `currentPassword` key is ignored (Zod strips unknown keys) so older clients
 * keep parsing.
 */
export const changeEmailSchema = z.object({
  newEmail: emailSchema,
});

/** Body for `POST /api/v1/auth/change-email/revert` (JSON or form). */
export const emailChangeRevertSchema = z.object({
  token: z.string().min(1, 'Revert token is required').max(512),
});

export const magicLinkRequestSchema = z.object({
  email: emailSchema,
});

export const magicLinkConsumeSchema = z.object({
  token: z.string().min(1, 'Magic link token is required'),
  resolvedTheme: resolvedThemeSchema.optional(),
});

export const verifyEmailSchema = z.object({
  token: z.string().min(1, 'Verification token is required'),
});

export const magicLinkSendSchema = forgotPasswordSchema;

export const refreshTokenRequestSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token is required'),
});

// API Response validation schemas
export const authResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    username: z.string().optional(),
    emailVerified: z.boolean().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  accessToken: z.string().min(1, 'Access token is required'),
  refreshToken: z.string().min(1, 'Refresh token is required'),
  message: z.string().optional(),
});

export const errorResponseSchema = z.object({
  error: z.string(),
  message: z.string(),
  details: z
    .array(
      z.object({
        field: z.string(),
        message: z.string(),
      }),
    )
    .optional(),
  code: z.string().optional(),
  timestamp: z.string().optional(),
  requestId: z.string().optional(),
});

export const userResponseSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  username: z.string().optional(),
  emailVerified: z.boolean(),
  preferences: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /**
   * WEB-39 — linked Advertiser id when the user has completed self-serve
   * onboarding; `null` / omitted means the account is consumer-only.
   */
  advertiserId: z.string().min(1).max(128).nullable().optional(),
});

// Loading state types for frontend forms
export const loadingStateSchema = z.object({
  isLoading: z.boolean().default(false),
  isSubmitting: z.boolean().default(false),
  isValidating: z.boolean().default(false),
  loadingMessage: z.string().optional(),
});

// Form state management
export const formStateSchema = z.object({
  isDirty: z.boolean().default(false),
  isValid: z.boolean().default(false),
  isSubmitted: z.boolean().default(false),
  submitCount: z.number().default(0),
  errors: z.record(z.string(), z.string()).default({}),
  touchedFields: z.record(z.string(), z.boolean()).default({}),
});
// Social login schema
export const socialLoginSchema = z.object({
  provider: z.enum(['google', 'facebook', 'apple', 'github'], {
    message: 'Invalid provider',
  }),
  accessToken: z.string().min(1, 'Access token is required'),
  idToken: z.string().optional(),
});

// Provider link/unlink schemas (API-1227).
// FRE-864 widened the enum with `x` and `tiktok`. Unlink is a row delete that
// maps `provider` → `OAUTH_<PROVIDER>` (see backend `toIdentityType`); the
// Prisma `IdentityType` enum already carries `OAUTH_X` / `OAUTH_TIKTOK`, so
// accepting these at the unlink boundary is safe and does NOT depend on the
// X / TikTok *link* providers being live (API-1523 / API-1524). `instagram`
// is intentionally excluded: the IG identity is captured as a Meta sub-scope
// (badge only) and is not independently linkable/unlinkable.
export const oauthProviderNameSchema = z.enum(
  ['google', 'facebook', 'apple', 'github', 'x', 'tiktok'],
  {
    message: 'Invalid OAuth provider name',
  },
);

export const linkProviderSchema = z.object({
  provider: oauthProviderNameSchema,
  redirectUrl: z.string().url('Must be a valid URL').optional(),
});

export const unlinkProviderParamsSchema = z.object({
  provider: oauthProviderNameSchema,
});

// Identity model schemas (canonical identity system)
// Derived from @loci/types IdentityType enum — single source of truth (Prisma → TS enum → Zod)
export const identityTypeSchema = z.nativeEnum(IdentityType);

/**
 * WebAuthn signature counter is a 32-bit unsigned integer (RFC 8809 / WebAuthn L3).
 * Clamping here prevents overflowed values from silently bypassing monotonic-counter
 * checks in the future assertion handler (FRE-689).
 */
const WEBAUTHN_COUNTER_MAX = 4_294_967_295;

/**
 * WebAuthn `AuthenticatorTransport` values from the L3 spec. Constrained here so
 * we never persist garbage transports from a malformed registration request.
 */
export const passkeyAuthenticatorTransportSchema = z.enum([
  'usb',
  'nfc',
  'ble',
  'internal',
  'hybrid',
  'smart-card',
]);

/**
 * Passkey (WebAuthn) credential metadata stored in `Identity.metadata` JSON.
 *
 * Shape persisted alongside a PASSKEY identity:
 * - `providerSubject` holds the credential ID (base64url-encoded).
 * - `metadata` holds this object.
 *
 * Field sources:
 * - `publicKey`     — base64url-encoded COSE public key returned by the authenticator.
 * - `counter`       — signature counter; monotonically increases per successful assertion
 *                     (32-bit unsigned, max `4,294,967,295`).
 * - `transports`    — optional authenticator transports, constrained to the WebAuthn L3 set.
 * - `aaguid`        — authenticator attestation GUID.
 * - `backupEligible`/`backupState` — WebAuthn L3 credential backup flags.
 */
export const passkeyCredentialMetadataSchema = z.object({
  publicKey: z.string().min(1, 'Passkey publicKey is required'),
  counter: z.number().int().min(0).max(WEBAUTHN_COUNTER_MAX),
  transports: z.array(passkeyAuthenticatorTransportSchema).optional(),
  aaguid: z.string().min(1, 'Passkey aaguid is required'),
  backupEligible: z.boolean(),
  backupState: z.boolean(),
});

export type PasskeyAuthenticatorTransport = z.infer<typeof passkeyAuthenticatorTransportSchema>;

export type PasskeyCredentialMetadata = z.infer<typeof passkeyCredentialMetadataSchema>;

/**
 * Empty body schema for `POST /api/v1/auth/passkey/register/begin`.
 *
 * The route is authenticated so the user is identified by the JWT, not the body.
 * Exported so both the server and clients share one source of truth.
 */
export const passkeyRegisterBeginSchema = z.object({}).strict();

/**
 * Request body for `POST /api/v1/auth/passkey/register/finish`.
 *
 * Mirrors `@simplewebauthn/browser`'s `RegistrationResponseJSON`. We intentionally
 * validate shape (required keys + types) rather than the precise base64url format of
 * each field — `@simplewebauthn/server.verifyRegistrationResponse` performs the
 * cryptographic validation and will reject malformed bytes. Keeping the schema loose
 * here avoids rejecting legitimate browser variants (e.g. optional `authenticatorAttachment`).
 */
export const passkeyRegisterFinishSchema = z.object({
  id: z.string().min(1, 'Credential id is required'),
  rawId: z.string().min(1, 'Credential rawId is required'),
  type: z.literal('public-key'),
  authenticatorAttachment: z.enum(['platform', 'cross-platform']).optional(),
  clientExtensionResults: z.record(z.string(), z.unknown()).default({}),
  response: z.object({
    clientDataJSON: z.string().min(1, 'clientDataJSON is required'),
    attestationObject: z.string().min(1, 'attestationObject is required'),
    // Accept any string so browser-reported transports we don't strictly recognize
    // (e.g. Chrome on Android may send the legacy 'cable' for caBLE/hybrid) still
    // reach `verifyRegistrationResponse`. Values outside the WebAuthn L3 set are
    // stripped before persistence (see passkey.ts). Strict enum applies only to
    // `passkeyCredentialMetadataSchema`.
    transports: z.array(z.string()).optional(),
    publicKeyAlgorithm: z.number().int().optional(),
    publicKey: z.string().optional(),
    authenticatorData: z.string().optional(),
  }),
});

/**
 * Inferred request body type for `POST /api/v1/auth/passkey/register/begin`.
 *
 * The endpoint is authenticated; the user is identified by the JWT rather than the
 * body, so the payload is an empty object. Inferred from
 * {@link passkeyRegisterBeginSchema}.
 */
export type PasskeyRegisterBeginInput = z.infer<typeof passkeyRegisterBeginSchema>;

/**
 * Inferred request body type for `POST /api/v1/auth/passkey/register/finish`.
 *
 * Mirrors `@simplewebauthn/browser`'s `RegistrationResponseJSON`. Request-level
 * validation is deliberately loose so `@simplewebauthn/server.verifyRegistrationResponse`
 * can do the cryptographic checks. Inferred from {@link passkeyRegisterFinishSchema}.
 */
export type PasskeyRegisterFinishInput = z.infer<typeof passkeyRegisterFinishSchema>;

// Role enum schema — single source of truth (Prisma → TS enum → Zod)
export const roleSchema = z.nativeEnum(Role);

export const identitySchema = z.object({
  id: z.string(),
  userId: z.string(),
  type: identityTypeSchema,
  providerSubject: z.string().nullable(),
  hashedCredential: z.string().nullable(),
  emailVerified: z.boolean().default(false),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// Response schema omits hashedCredential to prevent leaking bcrypt hashes to clients
export const identityResponseSchema = identitySchema.omit({ hashedCredential: true });

export const createIdentitySchema = z
  .object({
    userId: z.string().min(1, 'User ID is required'),
    type: identityTypeSchema,
    providerSubject: z.string().nullable().optional(),
    hashedCredential: z.string().nullable().optional(),
    emailVerified: z.boolean().default(false),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.type === 'PASSWORD') return !!data.hashedCredential;
      return true;
    },
    { message: 'PASSWORD identity requires a hashed credential', path: ['hashedCredential'] },
  )
  .refine(
    (data) => {
      if (data.type.startsWith('OAUTH_')) return !!data.providerSubject;
      return true;
    },
    { message: 'OAuth identity requires a provider subject', path: ['providerSubject'] },
  )
  .refine(
    (data) => {
      if (data.type === IdentityType.PASSKEY) return !!data.providerSubject;
      return true;
    },
    {
      message: 'PASSKEY identity requires a provider subject (credential ID)',
      path: ['providerSubject'],
    },
  );

// Profile update schema (firstName/lastName/username/website — used by other consumers)
export const updateProfileSchema = z.object({
  firstName: nameSchema.optional(),
  lastName: nameSchema.optional(),
  username: usernameSchema.optional(),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  website: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  location: z.string().max(100, 'Location must be less than 100 characters').optional(),
});

/**
 * Schema for PUT /api/v1/auth/profile — single name field (2–50 chars), bio (max 500), location (optional).
 */
export const profileUpdateSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be at most 50 characters'),
  bio: z.string().trim().max(500, 'Bio must be at most 500 characters').optional(),
  location: z.string().trim().max(100, 'Location must be at most 100 characters').optional(),
});

// User preferences schema
export const userPreferencesSchema = z.object({
  notifications: z
    .object({
      email: z.boolean().default(true),
      push: z.boolean().default(true),
      sms: z.boolean().default(false),
      // Push sub-toggles
      pushReminders: z.boolean().default(true),
      pushPriceDrops: z.boolean().default(true),
      pushCategoryUpdates: z.boolean().default(false),
      // Quiet hours
      quietHoursEnabled: z.boolean().default(false),
      quietHoursStart: z
        .string()
        .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Invalid time format (HH:MM)')
        .default('22:00'),
      quietHoursEnd: z
        .string()
        .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Invalid time format (HH:MM)')
        .default('08:00'),
      // Email sub-toggles
      emailDigest: z.boolean().default(true),
      emailSavedUpdates: z.boolean().default(true),
    })
    .default({ email: true, push: true, sms: false }),
  privacy: z
    .object({
      profileVisible: z.boolean().default(true),
      showActivity: z.boolean().default(true),
      locationEnabled: z.boolean().default(true),
      analyticsEnabled: z.boolean().default(true),
      trackBrowsing: z.boolean().default(true),
    })
    .default({
      profileVisible: true,
      showActivity: true,
      locationEnabled: true,
      analyticsEnabled: true,
      trackBrowsing: true,
    }),
  location: z
    .object({
      lat: z.number().min(-90).max(90),
      lng: z.number().min(-180).max(180),
      radius: z.number().min(1).max(100).default(25),
    })
    .optional(),
  /**
   * Settings-surface preferences persisted on `User.preferences` and read back
   * by `GET /api/v1/auth/me`. These MUST be declared here or `z.object` strips
   * them on the client read-back parse (`mapUserFromGetMe`), which is what made
   * theme/date-format/timezone/default-location silently vanish on reload.
   */
  theme: z.enum(['light', 'dark', 'system']).optional(),
  dateFormat: z.enum(['12h', '24h']).optional(),
  timezone: z.string().min(1).max(64).optional(),
  preferredCategories: z.array(z.string().min(1).max(64)).max(20).optional(),
  radiusKm: z.number().min(1).max(200).optional(),
  locationLabel: z.string().max(200).optional(),
  /**
   * ISO 8601 timestamp of when the user finished the onboarding takeover
   * (FRE-801). Server-authoritative; absence means the user has not been
   * through onboarding yet and the gate should redirect them. Backend stamps
   * the value via `new Date().toISOString()` (always `Z` UTC), but
   * `{ offset: true }` keeps the validator permissive enough for any
   * future ISO-8601-with-offset producer (e.g. `+00:00` from a different
   * stamper) so the schema doesn't lock the boundary too tight.
   */
  onboardingCompletedAt: z.string().datetime({ offset: true }).optional(),
});

// JWT payload schema (tokenVersion/sessionId default for legacy tokens without the claim)
export const jwtPayloadSchema = z.object({
  id: z.string(),
  email: emailSchema,
  type: z.enum(['access', 'refresh']),
  iat: z.number(),
  exp: z.number(),
  tokenVersion: z.number().int().nonnegative().default(0),
  sessionId: z.string().optional(),
  /** Omitted on legacy tokens — parsed output defaults to USER. */
  role: jwtRoleClaimSchema.optional().default(DEFAULT_JWT_ROLE),
  /**
   * WEB-39 — optional advertiser scope. Present when the user is linked to an
   * `Advertiser` row (`User.advertiserId`). Omitted on legacy tokens and on
   * consumer-only accounts that have not completed /advertise onboarding.
   */
  advertiserId: z.string().min(1).max(128).optional(),
});

// Client type enum matching Prisma ClientType
export const clientTypeSchema = z.enum(['WEB', 'MOBILE', 'API']);

// Session model (canonical identity model — maps to Prisma Session)
export const sessionModelSchema = z.object({
  id: z.string(),
  userId: z.string(),
  refreshTokenFamily: z.string(),
  deviceInfo: z.record(z.string(), z.unknown()).nullable().optional(),
  clientType: clientTypeSchema,
  issuedAt: z.date(),
  lastActiveAt: z.date(),
  expiresAt: z.date(),
  revokedAt: z.date().nullable().optional(),
  revokeReason: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * How the current session was opened (API-1808). Only password sign-ins are
 * stamped today; every other method leaves `signInMethod` absent.
 */
export const sessionSignInMethodSchema = z.enum(['password']);

/** Device/session blob returned on GET /api/v1/auth/me (`session` field). Dates are ISO 8601 strings. */
export const authMeCurrentSessionSchema = z.object({
  sessionId: z.string(),
  deviceInfo: z.record(z.string(), z.unknown()).nullable(),
  clientType: clientTypeSchema,
  lastActiveAt: z.string(),
  issuedAt: z.string(),
  /** Present only when the session was opened with that method (API-1808). */
  signInMethod: sessionSignInMethodSchema.optional(),
  /**
   * True when the web should offer the passwordless migration banner: the
   * session came from a password sign-in and the account is not an E2E or
   * agent principal (API-1808). Optional for older servers.
   */
  passwordlessMigrationEligible: z.boolean().optional(),
});

/** One row in GET /api/v1/auth/sessions. Dates are ISO 8601 strings. */
export const authActiveSessionListItemSchema = z.object({
  id: z.string(),
  deviceInfo: z.record(z.string(), z.unknown()).nullable(),
  clientType: clientTypeSchema,
  lastActiveAt: z.string(),
  issuedAt: z.string(),
  isCurrent: z.boolean(),
});

export const authSessionsListResponseSchema = z.object({
  sessions: z.array(authActiveSessionListItemSchema),
});

// Refresh token model (maps to Prisma RefreshToken)
export const refreshTokenModelSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  tokenHash: z.string(),
  familyId: z.string(),
  generation: z.number().int().min(0),
  issuedAt: z.date(),
  expiresAt: z.date(),
  usedAt: z.date().nullable().optional(),
  replacedByTokenId: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Role and permission schemas
export const permissionSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Permission name is required'),
  description: z.string().optional(),
  resource: z.string().min(1, 'Resource is required'),
  action: z.string().min(1, 'Action is required'),
});

export const roleDefinitionSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Role name is required'),
  description: z.string().optional(),
  permissions: z.array(permissionSchema),
  isDefault: z.boolean().default(false),
});

// Export type inference for TypeScript
export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type ChangePasswordRequestInput = z.infer<typeof changePasswordRequestSchema>;

/**
 * Inferred request body type for `changeEmailSchema`.
 * Downstream consumers should treat this as the public
 * `POST /api/v1/auth/change-email/request` body (legacy: `PUT /api/v1/auth/email`).
 */
export type ChangeEmailInput = z.infer<typeof changeEmailSchema>;

export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestSchema>;
export type MagicLinkConsumeInput = z.infer<typeof magicLinkConsumeSchema>;
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;
export type MagicLinkSendInput = z.infer<typeof magicLinkSendSchema>;
export type RefreshTokenInput = z.infer<typeof refreshTokenRequestSchema>;
export type SocialLoginInput = z.infer<typeof socialLoginSchema>;
export type OAuthProviderName = z.infer<typeof oauthProviderNameSchema>;
export type LinkProviderInput = z.infer<typeof linkProviderSchema>;
export type UnlinkProviderParams = z.infer<typeof unlinkProviderParamsSchema>;
export type IdentityTypeInput = z.infer<typeof identityTypeSchema>;
export type IdentityInput = z.infer<typeof identitySchema>;
export type IdentityResponse = z.infer<typeof identityResponseSchema>;
export type CreateIdentityInput = z.infer<typeof createIdentitySchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;
export type UserPreferencesInput = z.infer<typeof userPreferencesSchema>;
export type JwtPayloadInput = z.infer<typeof jwtPayloadSchema>;
export type ClientTypeInput = z.infer<typeof clientTypeSchema>;
export type SessionModelInput = z.infer<typeof sessionModelSchema>;
export type SessionSignInMethod = z.infer<typeof sessionSignInMethodSchema>;
export type AuthMeCurrentSession = z.infer<typeof authMeCurrentSessionSchema>;
export type AuthActiveSessionListItem = z.infer<typeof authActiveSessionListItemSchema>;
export type AuthSessionsListResponse = z.infer<typeof authSessionsListResponseSchema>;
export type RefreshTokenModelInput = z.infer<typeof refreshTokenModelSchema>;
export type PermissionInput = z.infer<typeof permissionSchema>;
export type RoleDefinitionInput = z.infer<typeof roleDefinitionSchema>;
export type RoleInput = z.infer<typeof roleSchema>;

// API Response types
export type AuthResponse = z.infer<typeof authResponseSchema>;
export type AuthErrorResponse = z.infer<typeof errorResponseSchema>; // Renamed to avoid conflict
export type UserResponse = z.infer<typeof userResponseSchema>;
export type LoadingState = z.infer<typeof loadingStateSchema>;
export type FormState = z.infer<typeof formStateSchema>;

// Aliases for backward compatibility with backend imports
export type LoginRequest = LoginInput;
export type RegisterRequest = RegisterInput;
/** @deprecated Use RefreshTokenInput instead */
export type RefreshTokenRequest = RefreshTokenInput;

// Frontend integration utilities
export const createAuthFormValidator = <T extends z.ZodRawShape>(schema: z.ZodObject<T>) => {
  return {
    validate: (data: unknown) => validateAndFormat(schema, data),
    validateField: (fieldName: string, value: unknown) => {
      try {
        const fieldSchema = (schema.shape as z.ZodRawShape)[fieldName];
        if (!fieldSchema) {
          return { success: false, error: 'Field not found' };
        }
        fieldSchema.parse(value);
        return { success: true };
      } catch (error) {
        if (error instanceof z.ZodError) {
          return {
            success: false,
            error: error.issues.map((e: z.ZodIssue) => e.message).join('. ') || 'Validation failed',
          };
        }
        return { success: false, error: 'Validation failed' };
      }
    },
    getFieldSchema: (fieldName: string) => (schema.shape as z.ZodRawShape)[fieldName],
  };
};

// Optimistic updates helper
export const createOptimisticUpdate = <T>(
  initialData: T,
  updateFn: (data: T) => T,
  rollbackFn?: (data: T) => T,
) => {
  let currentData = initialData;
  let isOptimistic = false;

  return {
    apply: () => {
      if (!isOptimistic) {
        currentData = updateFn(currentData);
        isOptimistic = true;
      }
      return currentData;
    },
    rollback: () => {
      if (isOptimistic && rollbackFn) {
        currentData = rollbackFn(currentData);
        isOptimistic = false;
      }
      return currentData;
    },
    commit: () => {
      isOptimistic = false;
      return currentData;
    },
    getCurrentData: () => currentData,
    isOptimistic: () => isOptimistic,
  };
};

// Error boundary helper types
export type AuthErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: {
    componentStack?: string;
    errorBoundary?: string;
  } | null;
  retryCount: number;
  maxRetries: number;
};

// Helper function to extract error code from error message or object
const extractErrorCode = (error: Error): string | null => {
  // Try to extract from error message first
  const errorCodes = Object.values(ERROR_CODES);
  for (const code of errorCodes) {
    if (error.message.includes(code)) {
      return code;
    }
  }

  // Try to extract from error object if it has a code property
  if ('code' in error && typeof (error as Record<string, unknown>).code === 'string') {
    return (error as Record<string, unknown>).code as string;
  }

  return null;
};

export const createAuthErrorHandler = (maxRetries = 3) => {
  return {
    shouldRetry: (error: Error, retryCount: number) => {
      // Retry for network errors but not for validation errors
      const isNetworkError =
        error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('timeout');
      return isNetworkError && retryCount < maxRetries;
    },

    getErrorMessage: (error: Error): string => {
      const errorCode = extractErrorCode(error);

      // Use structured error codes for reliable mapping
      switch (errorCode) {
        case ERROR_CODES.VALIDATION_FAILED:
          return 'Please check your information and try again.';
        case ERROR_CODES.INVALID_CREDENTIALS:
          return VALIDATION_MESSAGES.AUTH.INVALID_CREDENTIALS;
        case ERROR_CODES.EMAIL_ALREADY_EXISTS:
          return VALIDATION_MESSAGES.AUTH.EMAIL_EXISTS;
        case ERROR_CODES.RATE_LIMITED:
          return 'Too many attempts. Please wait a moment and try again.';
        case ERROR_CODES.EMAIL_NOT_VERIFIED:
          return 'Please verify your email address before signing in.';
        case ERROR_CODES.ACCOUNT_LOCKED:
          return 'Your account has been locked. Please contact support.';
        case ERROR_CODES.TOKEN_EXPIRED:
          return 'Your session has expired. Please sign in again.';
        case ERROR_CODES.TOKEN_INVALID:
          return 'Invalid session. Please sign in again.';
        case ERROR_CODES.USER_NOT_FOUND:
          return 'No account found with this email address.';
        case ERROR_CODES.WEAK_PASSWORD:
          return 'Password is too weak. Please choose a stronger password.';
        case ERROR_CODES.UNAUTHORIZED:
          return 'You are not authorized to perform this action.';
        case ERROR_CODES.FORBIDDEN:
          return 'Access denied. You do not have permission to perform this action.';
        default:
          // Fallback to network error detection
          if (error.message.includes('fetch') || error.message.includes('network')) {
            return 'Network error. Please check your connection and try again.';
          }
          return 'Something went wrong. Please try again.';
      }
    },

    categorizeError: (error: Error) => {
      const errorCode = extractErrorCode(error);

      // Use structured error codes for reliable categorization
      switch (errorCode) {
        case ERROR_CODES.VALIDATION_FAILED:
        case ERROR_CODES.INVALID_INPUT:
        case ERROR_CODES.REQUIRED_FIELD:
        case ERROR_CODES.INVALID_FORMAT:
        case ERROR_CODES.WEAK_PASSWORD:
          return 'validation';
        case ERROR_CODES.INVALID_CREDENTIALS:
        case ERROR_CODES.EMAIL_NOT_VERIFIED:
        case ERROR_CODES.ACCOUNT_LOCKED:
        case ERROR_CODES.TOKEN_EXPIRED:
        case ERROR_CODES.TOKEN_INVALID:
        case ERROR_CODES.USER_NOT_FOUND:
        case ERROR_CODES.UNAUTHORIZED:
        case ERROR_CODES.FORBIDDEN:
          return 'auth';
        case ERROR_CODES.RATE_LIMITED:
          return 'rate_limit';
        default:
          // Fallback to network error detection
          if (error.message.includes('fetch') || error.message.includes('network')) {
            return 'network';
          }
          return 'unknown';
      }
    },
  };
};
